'use client';

import { ROLE_TYPES } from '@/@studio-ai/constants';
import { UseAutoScrollProps } from '@/@studio-ai/props';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const BOTTOM_THRESHOLD_PX = 100;

export function useAutoScroll({
    messages,
    conversationHistoryLoading,
    isLoading,
    onScrollStateChange,
    scrollToBottomRef,
    scrollToTopRef,
    startAtTop = false,
}: UseAutoScrollProps) {
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [isAtTop, setIsAtTop] = useState(true);
    const isAtBottomRef = useRef(true);
    const isAtTopRef = useRef(true);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const updateIsAtBottom = useCallback(
        (value: boolean) => {
            isAtBottomRef.current = value;
            setIsAtBottom(value);
            onScrollStateChange?.(isAtTopRef.current, value);
        },
        [onScrollStateChange],
    );

    const updateIsAtTop = useCallback(
        (value: boolean) => {
            isAtTopRef.current = value;
            setIsAtTop(value);
            onScrollStateChange?.(value, isAtBottomRef.current);
        },
        [onScrollStateChange],
    );

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }, []);

    const scrollToTop = useCallback((behavior: ScrollBehavior = 'smooth') => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: 0, behavior });
    }, []);

    if (scrollToBottomRef) {
        scrollToBottomRef.current = () => scrollToBottom('smooth');
    }

    if (scrollToTopRef) {
        scrollToTopRef.current = () => scrollToTop('smooth');
    }

    // Track isAtBottom and isAtTop via scroll events on the container.
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const handleScroll = () => {
            const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
            const atBottom = distance <= BOTTOM_THRESHOLD_PX;
            const atTop = el.scrollTop <= 0;
            if (atBottom !== isAtBottomRef.current) {
                updateIsAtBottom(atBottom);
            }
            if (atTop !== isAtTopRef.current) {
                updateIsAtTop(atTop);
            }
        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [updateIsAtBottom, updateIsAtTop]);

    // A chat that opens on something to read from its start (an insight card's
    // finding, docs/pdlc/insights-dive-deep.md) opens at the top and is not
    // pinned to the end while its charts render. Set before the observer's
    // first callback; the reader's first send ends it.
    const holdTopRef = useRef(startAtTop);
    useLayoutEffect(() => {
        holdTopRef.current = startAtTop;
        const el = scrollContainerRef.current;
        if (startAtTop && el) el.scrollTop = 0;
    }, [startAtTop]);

    // ResizeObserver on the content wrapper and the container: when content
    // grows, or the footer's padding grows, and the user is near the bottom, pin
    // scroll position to the bottom instantly (frame-accurate tracking during
    // FLIP height animations).
    useEffect(() => {
        const content = contentRef.current;
        const container = scrollContainerRef.current;
        if (!content || !container) return;

        const observer = new ResizeObserver(() => {
            if (holdTopRef.current || !isAtBottomRef.current) return;
            container.scrollTop = container.scrollHeight - container.clientHeight;
        });

        observer.observe(content);
        // The container too: the footer below the chat (the input, and a
        // feasibility check's panel that loads after the chat has scrolled down)
        // pads the container, not the content. Without this the last reply's
        // actions (thumbs, copy) end up under the footer (staging, 2026-10-22).
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    // Scroll to bottom when messages change and auto-scroll conditions are met.
    const shouldAutoScroll = useCallback(() => {
        if (conversationHistoryLoading) return false;
        if (messages.length === 0) return false;

        const lastMessage = messages[messages.length - 1];

        if (lastMessage.role === ROLE_TYPES.USER) return true;

        if (!isAtBottomRef.current) return false;

        if (lastMessage.role === ROLE_TYPES.ASSISTANT && lastMessage.isStreaming) return true;
        if (isLoading) return true;
        if (lastMessage.role === ROLE_TYPES.ASSISTANT && lastMessage._animate) return true;

        return false;
    }, [messages, conversationHistoryLoading, isLoading]);

    useEffect(() => {
        if (shouldAutoScroll()) {
            scrollToBottom('instant');
        }
    }, [messages, shouldAutoScroll, scrollToBottom]);

    // Scroll to bottom once conversation history finishes loading.
    const prevLoadingRef = useRef(conversationHistoryLoading);
    useEffect(() => {
        if (prevLoadingRef.current && !conversationHistoryLoading && messages.length > 0) {
            scrollToBottom('instant');
        }
        prevLoadingRef.current = conversationHistoryLoading;
    }, [conversationHistoryLoading, messages.length, scrollToBottom]);

    return {
        isAtTop,
        scrollContainerRef,
        contentRef,
        isAtBottom,
        scrollToBottom,
    };
}
